// P1B-FE — coded live-tile states beyond online/offline. Pure + testable.
//
// SINGLE IMPORTER, DELIBERATELY — reviewed 2026-08-03, do not "collapse" this.
//
// This module has one importer (pages/LiveView.tsx) and CameraFeedPlayer has no
// coded tile states. That LOOKS like the presentation twin of the two-player P0
// defect, where streamStaleness lived in CameraFeedPlayer and /live ran a second
// detector-less player. It is not the same thing, and the difference matters:
//
//   * The P0 defect was ONE PROPERTY implemented in one place and MISSING in the
//     other, on two routes that both needed it. Real duplication, real gap.
//   * Here the single importer is a consequence of WHERE THE DATA COMES FROM.
//     `tile_state` is a field on the `ensureLiveView` (warm-up) response, and
//     `ensureLiveView` is called ONLY by pages/LiveView.tsx — verified: it has
//     the sole call site. CameraFeedPlayer never warms up, so it never receives
//     a tile_state to render. There is nothing to collapse; a shared helper
//     would have one caller either way.
//
// The real question hiding here is a FEATURE question, not a defect: should
// /grid also warm up and surface capacity/degraded/failover? That is a product
// decision about whether the grid should drive relays, and it belongs on the
// backlog, not in a refactor.
// Colors reuse the existing palette (amber / accent-orange / pink); NO restyle.
export type P1BTileState = "DEGRADED" | "CAPACITY" | "FAILOVER";

export interface TileStateMeta {
  label: string;
  color: string;
}

/** Map the backend `tile_state` hint (from the ensure response) to an operator
 *  label + dot color. Returns null for a normal (OK/unknown) tile. */
export function tileStateMeta(
  s: string | null | undefined,
): TileStateMeta | null {
  switch (s) {
    case "DEGRADED":
      return { label: "Degraded quality", color: "#fbbf24" }; // amber-400
    case "CAPACITY":
      return { label: "NVR at stream limit", color: "#ff6d00" }; // accent
    case "FAILOVER":
      return { label: "Failover active", color: "#ec4899" }; // pink-500
    // Deliberately NOT a shade of "starting". This says the readiness answer
    // is missing, not that the stream is coming up — the stream may already
    // be live. Slate reads as "unknown", not as a warning about the camera,
    // because the fault is in our check, not in the feed.
    case "CHECK_FAILED":
      return { label: "Readiness check failed", color: "#94a3b8" }; // slate-400
    default:
      return null;
  }
}
